import { useEffect, useState } from 'react';
import { BookMarked, Database, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCollections } from '@/services/data-sources/get-collections';
import { Collection } from '@/types/source-types';
import { CollectionDocumentsList } from '@/pages/data-sources/collection-page/collection-documents-list';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { useOrgStore } from '@/stores/org-store';

// Sources collections are per-user: the list API only returns the requesting
// user's own.
export const SourcesPage = () => {
  const navigate = useOrgNavigate();
  const { currentOrg } = useOrgStore();
  const [sourcesCollection, setSourcesCollection] = useState<Collection | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await getCollections();
        if (cancelled) return;
        if (Array.isArray(response)) {
          const sources =
            response.find((c) => c.system_type === 'sources') ?? null;
          setSourcesCollection(sources);
        } else {
          setError(response.detail || 'Failed to load collections');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load sources');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.orgid]);

  return (
    <div className='mx-auto w-full max-w-6xl space-y-6 p-6'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <h1 className='flex items-center gap-2 text-2xl font-semibold'>
            <BookMarked className='size-6' />
            My Sources
          </h1>
          <p className='mt-1 max-w-3xl text-sm text-muted-foreground'>
            Your personal citation library. Sources are saved here by your
            workflows with a connected Citation Store tool, and can be searched
            and reused by your other workflows. It is private to you. Each
            record shows which workflow and run saved it, and why.
          </p>
        </div>
        {sourcesCollection && (
          <Button
            variant='outline'
            onClick={() => navigate(`/data-sources/${sourcesCollection.id}`)}
          >
            <Database className='size-4' />
            Open collection
            <ExternalLink className='size-3' />
          </Button>
        )}
      </div>

      {loading && (
        <p className='text-sm text-muted-foreground'>Loading sources…</p>
      )}
      {error && (
        <p className='text-sm text-rose-600 dark:text-rose-400'>{error}</p>
      )}
      {!loading && !error && !sourcesCollection && (
        <div className='rounded-lg border border-dashed px-6 py-10 text-center text-sm text-muted-foreground'>
          Your personal Sources collection hasn&apos;t been initialized yet.
          Open Data Collections once to set it up, then come back here.
        </div>
      )}

      {sourcesCollection && (
        <CollectionDocumentsList
          collectionId={sourcesCollection.id}
          variant='sources'
        />
      )}
    </div>
  );
};

export default SourcesPage;
